import { useState, useMemo, memo } from "react";
import type { ReactNode } from "react";
import { useSearchParams, NavLink, useNavigate } from "react-router-dom";
import styles from "./Dashboard.module.css";
import { JOB_ACTIVE, JOB_COMPLETED, STATUS_PENDING } from "../constants";
import { formatTime } from "../utils/formatTime";
import { getRoleFlags } from "../hooks/useRole";

const shortTime = (t: string) => formatTime(t).replace(/\s*\(.*\)/, "");

const MS_PER_DAY = 86400000;

function daysUntil(dateStr: string) {
  const jobDate = new Date(dateStr + "T00:00:00");
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((jobDate.getTime() - now.getTime()) / MS_PER_DAY);
}

function formatJobDate(dateStr: string) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  const monthShort = d.toLocaleString("default", { month: "short" });
  const dayNum = d.getDate();
  return `${monthShort} ${dayNum}`;
}

function getInitials(userId: string, users: any[]) {
  const user = (users || []).find((u) => u.id === userId);
  if (!user || !user.name) return "?";
  const parts = user.name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : parts[0][0].toUpperCase();
}

function matchesSearch(job: any, query: string) {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    (job.title || "").toLowerCase().includes(q) ||
    (job.location || "").toLowerCase().includes(q) ||
    (job.entity || "").toLowerCase().includes(q) ||
    (job.date || "").toLowerCase().includes(q)
  );
}

/* ---------- KanbanCard ---------- */

interface KanbanCardProps {
  job: any;
  borderColor?: string;
  dimmed?: boolean;
  tag?: ReactNode;
  tagClass?: string;
  statusLabel?: ReactNode;
  statusClass?: string;
  waitlistPosition?: number | null;
  attendanceText?: ReactNode;
  attendanceClass?: string;
  actions?: ReactNode;
  avatarStack?: ReactNode;
  fillMeter?: ReactNode;
  navigate: (_path: string) => void;
  search: string;
}

const KanbanCard = memo(function KanbanCard({
  job,
  borderColor,
  dimmed,
  tag,
  tagClass,
  statusLabel,
  statusClass,
  waitlistPosition,
  attendanceText,
  attendanceClass,
  actions,
  avatarStack,
  fillMeter,
  navigate,
  search,
}: KanbanCardProps) {
  const visible = matchesSearch(job, search);

  return (
    <div
      className={styles.card}
      style={{
        borderLeftColor: borderColor || "transparent",
        opacity: dimmed ? 0.55 : 1,
        display: visible ? undefined : "none",
      }}
    >
      <button
        className={styles.cardButton}
        onClick={() => navigate(`/jobs/${job.id}`)}
        aria-label={`View job: ${job.title}`}
      >
      {tag && (
        <span className={`${styles.cardTag} ${tagClass || ""}`}>{tag}</span>
      )}
      <div className={styles.cardTitle}>{job.title}</div>
      <div className={styles.cardWhen}>
        {formatJobDate(job.date)} &middot; {shortTime(job.startTime)} &ndash;{" "}
        {shortTime(job.endTime)}
      </div>
      <div className={styles.cardWhere}>{job.location || job.entity}</div>

      {statusLabel && (
        <span className={`${styles.cardStatus} ${statusClass || ""}`}>
          {statusLabel}
        </span>
      )}

      {waitlistPosition != null && (
        <span className={styles.cardWaitPos}>#{waitlistPosition} in line</span>
      )}

      {fillMeter}
      {avatarStack}

      {attendanceText && (
        <span className={`${styles.cardAttendance} ${attendanceClass || ""}`}>
          {attendanceText}
        </span>
      )}

      </button>
      <div className={styles.cardFooter}>
        <span className={styles.cardPay}>${job.payRate}/hr</span>
        <div className={styles.cardActions}>
          {actions}
        </div>
      </div>
    </div>
  );
});

/* ---------- AvatarStack ---------- */

function AvatarStack({ signups, slotsTotal, users }: { signups: string[]; slotsTotal: number; users: any[] }) {
  const filled = signups || [];
  const empty = Math.max(0, slotsTotal - filled.length);
  return (
    <div className={styles.avatarStack}>
      {filled.map((uid) => (
        <span key={uid} className={styles.avatarFilled} title={uid}>
          {getInitials(uid, users)}
        </span>
      ))}
      {Array.from({ length: empty }).map((_, i) => (
        <span key={`e${i}`} className={styles.avatarEmpty} />
      ))}
    </div>
  );
}

/* ---------- FillMeter ---------- */

function FillMeter({ filled, total }: { filled: number; total: number }) {
  const pct = total > 0 ? Math.min(100, (filled / total) * 100) : 0;
  return (
    <div className={styles.fillMeter}>
      <div className={styles.fillMeterBar} style={{ width: `${pct}%` }} />
      <span className={styles.fillMeterLabel}>
        {filled}/{total} filled
      </span>
    </div>
  );
}

/* ---------- Dashboard ---------- */

export default function Dashboard({
  jobs,
  users,
  currentUser,
  onSignup,
  onWithdraw,
  onJoinWaitlist,
  onLeaveWaitlist,
  onEdit,
  loading,
}: any) {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view") || "board";
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const [timeFilter, setTimeFilter] = useState("upcoming");

  const setView = (v) => {
    if (v === "calendar") {
      navigate("/calendar");
      return;
    }
    setSearchParams({ view: v });
  };

  /* --- data computations --- */
  const todayStr = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  }, []);

  const activeJobs = useMemo(
    () => jobs.filter((j) => j.status === JOB_ACTIVE),
    [jobs]
  );

  // For officers, hide past jobs from browse views
  const officerUpcomingJobs = useMemo(
    () => activeJobs.filter((j) => j.date >= todayStr),
    [activeJobs, todayStr]
  );

  // Officers can see their own signups/waitlist even for private jobs (admin assigned them)
  const mySignups = useMemo(
    () => officerUpcomingJobs.filter((j) => (j.signups || []).includes(currentUser.id)),
    [officerUpcomingJobs, currentUser.id]
  );

  const myWaitlisted = useMemo(
    () =>
      officerUpcomingJobs.filter((j) => (j.waitlist || []).includes(currentUser.id)),
    [officerUpcomingJobs, currentUser.id]
  );

  // Available jobs hides private jobs — only admin can assign officers to those
  const availableJobs = useMemo(
    () =>
      officerUpcomingJobs.filter(
        (j) =>
          !j.isPrivate &&
          !(j.signups || []).includes(currentUser.id) &&
          !(j.waitlist || []).includes(currentUser.id) &&
          !(j.blockedOfficers || []).includes(currentUser.id)
      ),
    [officerUpcomingJobs, currentUser.id]
  );

  const completedJobs = useMemo(
    () =>
      jobs.filter(
        (j) =>
          j.status === JOB_COMPLETED &&
          (j.signups || []).includes(currentUser.id)
      ),
    [jobs, currentUser.id]
  );

  const pendingOfficers = useMemo(
    () => (users || []).filter((u) => u.status === STATUS_PENDING),
    [users]
  );

  const { canManageData: isAdmin, isAdmin: isTrulyAdmin } = getRoleFlags(currentUser?.role);

  /* --- admin time-filtered active jobs --- */
  const adminFilteredActive = useMemo(() => {
    if (timeFilter === "upcoming") return activeJobs.filter((j) => j.date >= todayStr);
    if (timeFilter === "past") return activeJobs.filter((j) => j.date < todayStr);
    return activeJobs; // "all"
  }, [activeJobs, todayStr, timeFilter]);

  /* --- admin-specific columns --- */
  const needsAttention = useMemo(() => {
    return adminFilteredActive
      .filter((j) => {
        const filled = (j.signups || []).length;
        const days = daysUntil(j.date);
        if (filled === 0) return true;
        if (days < 3 && filled < j.slotsTotal) return true;
        return false;
      })
      .sort((a, b) => {
        const aFilled = (a.signups || []).length;
        const bFilled = (b.signups || []).length;
        if (aFilled === 0 && bFilled !== 0) return -1;
        if (bFilled === 0 && aFilled !== 0) return 1;
        return daysUntil(a.date) - daysUntil(b.date);
      });
  }, [adminFilteredActive]);

  const needsAttentionIds = useMemo(
    () => new Set(needsAttention.map((j) => j.id)),
    [needsAttention]
  );

  const partiallyFilled = useMemo(
    () =>
      adminFilteredActive.filter((j) => {
        const filled = (j.signups || []).length;
        return (
          filled > 0 &&
          filled < j.slotsTotal &&
          !needsAttentionIds.has(j.id)
        );
      }),
    [adminFilteredActive, needsAttentionIds]
  );

  const fullyStaffed = useMemo(
    () =>
      adminFilteredActive.filter(
        (j) => (j.signups || []).length >= j.slotsTotal
      ),
    [adminFilteredActive]
  );

  const adminCompleted = useMemo(() => {
    const completed = jobs.filter((j) => j.status === JOB_COMPLETED);
    if (timeFilter === "upcoming") return completed.filter((j) => j.date >= todayStr);
    if (timeFilter === "past") return completed.filter((j) => j.date < todayStr);
    return completed;
  }, [jobs, todayStr, timeFilter]);

  /* --- admin urgency tag builder --- */
  const buildUrgencyTag = (job) => {
    const filled = (job.signups || []).length;
    const days = daysUntil(job.date);
    let dayLabel;
    if (days <= 0) dayLabel = "TODAY";
    else if (days === 1) dayLabel = "TOMORROW";
    else dayLabel = `${days} DAYS OUT`;

    if (filled === 0) return `0 OFFICERS \u2014 ${dayLabel}`;
    return `${filled} OF ${job.slotsTotal} FILLED \u2014 ${dayLabel}`;
  };

  /* --- officer urgency check --- */
  const isUrgent = (job) => {
    return (job.signups || []).length === 0 && daysUntil(job.date) < 3;
  };

  /* --- export CSV --- */
  const exportCSV = () => {
    const headers = [
      "Title",
      "Date",
      "Start",
      "End",
      "Location",
      "Entity",
      "Pay Rate",
      "Slots",
      "Filled",
      "Officers",
    ];
    const rows = activeJobs.map((j) => {
      const officerNames = (j.signups || [])
        .map((uid) => {
          const u = (users || []).find((x) => x.id === uid);
          return u ? u.name : uid;
        })
        .join("; ");
      return [
        j.title,
        j.date,
        j.startTime,
        j.endTime,
        j.location || "",
        j.entity || "",
        j.payRate,
        j.slotsTotal,
        (j.signups || []).length,
        officerNames,
      ];
    });

    const escape = (v) => {
      const s = String(v ?? "");
      return s.includes(",") || s.includes('"') || s.includes("\n")
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };

    const csv =
      [headers.map(escape).join(",")]
        .concat(rows.map((r) => r.map(escape).join(",")))
        .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `jobs-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* --- column definitions --- */
  const firstName = (currentUser.name || "").split(/\s+/)[0] || "there";

  const officerColumns = [
    {
      key: "available",
      label: "AVAILABLE",
      icon: "\u25CF",
      stripeClass: "stripeGreen",
      countClass: "countGreen",
      emptyText: "available shifts",
      items: availableJobs,
    },
    {
      key: "myshifts",
      label: "MY SHIFTS",
      icon: "\u2705",
      stripeClass: "stripeGreen",
      countClass: "countGreen",
      emptyText: "shifts yet",
      items: mySignups,
    },
    {
      key: "waitlisted",
      label: "WAITLISTED",
      icon: "\u23F3",
      stripeClass: "stripeAmber",
      countClass: "countAmber",
      emptyText: "waitlisted shifts",
      items: myWaitlisted,
    },
    {
      key: "completed",
      label: "COMPLETED",
      icon: "\u2713",
      stripeClass: "stripeGray",
      countClass: "countGray",
      emptyText: "completed shifts",
      items: completedJobs,
    },
  ];

  const adminColumns = [
    {
      key: "attention",
      label: "NEEDS ATTENTION",
      icon: "\u26A0\uFE0F",
      stripeClass: "stripeRed",
      countClass: "countRed",
      emptyText: "urgent items",
      items: needsAttention,
    },
    {
      key: "partial",
      label: "PARTIALLY FILLED",
      icon: "\uD83D\uDD36",
      stripeClass: "stripeAmber",
      countClass: "countAmber",
      emptyText: "partially filled jobs",
      items: partiallyFilled,
    },
    {
      key: "staffed",
      label: "FULLY STAFFED",
      icon: "\u2705",
      stripeClass: "stripeGreen",
      countClass: "countGreen",
      emptyText: "fully staffed jobs",
      items: fullyStaffed,
    },
    {
      key: "completed",
      label: "COMPLETED",
      icon: "\u2713",
      stripeClass: "stripeGray",
      countClass: "countGray",
      emptyText: "completed jobs",
      items: adminCompleted,
    },
  ];

  const columns = isAdmin ? adminColumns : officerColumns;

  const allEmpty =
    isAdmin &&
    view === "board" &&
    needsAttention.length === 0 &&
    partiallyFilled.length === 0 &&
    fullyStaffed.length === 0 &&
    adminCompleted.length === 0;

  /* --- render helpers for cards --- */
  const renderOfficerCard = (job: any, colKey: string) => {
    let borderColor = "transparent";
    let statusLabel: ReactNode = null;
    let statusClass = "";
    let waitlistPosition: number | null = null;
    let dimmed = false;
    let tag: ReactNode = null;
    let tagClass = "";
    let attendanceText: ReactNode = null;
    const attendanceClass = "";
    let actions: ReactNode = null;

    switch (colKey) {
      case "available": {
        const isFull = (job.signups || []).length >= job.slotsTotal;
        if (isUrgent(job)) {
          tag = "URGENT \u2014 0 officers";
          tagClass = styles.tagRed;
        }
        actions = isFull ? (
          <button
            className={styles.btnAmber}
            onClick={() => onJoinWaitlist(job.id)}
          >
            Join Waitlist
          </button>
        ) : (
          <button
            className={styles.btnGreen}
            onClick={() => onSignup(job.id)}
          >
            Sign Up
          </button>
        );
        break;
      }
      case "myshifts":
        borderColor = "#16a34a";
        statusLabel = "\u25CF Confirmed";
        statusClass = styles.statusGreen;
        actions = (
          <button
            className={styles.btnRed}
            onClick={() => onWithdraw(job.id)}
          >
            Withdraw
          </button>
        );
        break;
      case "waitlisted":
        borderColor = "#d97706";
        waitlistPosition =
          (job.waitlist || []).indexOf(currentUser.id) + 1;
        actions = (
          <button
            className={styles.btnGray}
            onClick={() => onLeaveWaitlist(job.id)}
          >
            Leave
          </button>
        );
        break;
      case "completed":
        dimmed = true;
        if (job.attendance && job.attendance[currentUser.id]) {
          attendanceText = `Attendance: ${job.attendance[currentUser.id]}`;
        }
        break;
      default:
        break;
    }

    return (
      <KanbanCard
        key={job.id}
        job={job}
        borderColor={borderColor}
        dimmed={dimmed}
        tag={tag}
        tagClass={tagClass}
        statusLabel={statusLabel}
        statusClass={statusClass}
        waitlistPosition={waitlistPosition}
        attendanceText={attendanceText}
        attendanceClass={attendanceClass}
        actions={actions}
        navigate={navigate}
        search={search}
      />
    );
  };

  const renderAdminCard = (job: any, colKey: string) => {
    let borderColor = "transparent";
    let dimmed = false;
    let tag: ReactNode = null;
    let tagClass = "";
    let attendanceText: ReactNode = null;
    let attendanceClass = "";
    let actions: ReactNode = null;
    const signups = job.signups || [];

    const avatarStack = (
      <AvatarStack
        signups={signups}
        slotsTotal={job.slotsTotal}
        users={users}
      />
    );

    const fillMeter = (
      <FillMeter filled={signups.length} total={job.slotsTotal} />
    );

    switch (colKey) {
      case "attention":
        borderColor = "#dc2626";
        tag = buildUrgencyTag(job);
        tagClass = styles.tagRed;
        actions = (
          <>
            <button
              className={styles.btnOutline}
              onClick={(e) => { e.stopPropagation(); onEdit(job); }}
            >
              Edit
            </button>
          </>
        );
        break;
      case "partial":
        borderColor = "#d97706";
        actions = (
          <button
            className={styles.btnOutline}
            onClick={(e) => { e.stopPropagation(); onEdit(job); }}
          >
            Edit
          </button>
        );
        break;
      case "staffed":
        borderColor = "#16a34a";
        actions = (
          <button
            className={styles.btnOutline}
            onClick={(e) => { e.stopPropagation(); onEdit(job); }}
          >
            Edit
          </button>
        );
        break;
      case "completed": {
        dimmed = true;
        const hasAttendance =
          job.attendance && Object.keys(job.attendance).length > 0;
        attendanceText = hasAttendance
          ? "Attendance logged"
          : "Attendance pending";
        attendanceClass = hasAttendance
          ? styles.attendanceGreen
          : styles.attendanceAmber;
        actions = (
          <NavLink
            to={`/jobs/${job.id}`}
            className={styles.btnOutline}
            onClick={(e) => e.stopPropagation()}
          >
            {hasAttendance ? "View report" : "Record attendance"}
          </NavLink>
        );
        break;
      }
      default:
        break;
    }

    return (
      <KanbanCard
        key={job.id}
        job={job}
        borderColor={borderColor}
        dimmed={dimmed}
        tag={tag}
        tagClass={tagClass}
        attendanceText={attendanceText}
        attendanceClass={attendanceClass}
        actions={actions}
        avatarStack={avatarStack}
        fillMeter={colKey !== "attention" ? fillMeter : null}
        navigate={navigate}
        search={search}
      />
    );
  };

  const renderCard = (job: any, colKey: string) =>
    isAdmin ? renderAdminCard(job, colKey) : renderOfficerCard(job, colKey);

  /* --- all jobs flat list for list view --- */
  const allListJobs = useMemo(() => {
    const seen = new Set<string>();
    const result: { job: any; colKey: string }[] = [];
    for (const col of columns) {
      for (const job of col.items) {
        if (!seen.has(job.id)) {
          seen.add(job.id);
          result.push({ job, colKey: col.key });
        }
      }
    }
    return result;
  }, [columns]);

  if (loading) {
    return <p className={styles.loading}>Loading shifts...</p>;
  }

  return (
    <div className={styles.board}>
      {/* Sub-bar */}
      <div className={styles.subBar}>
        <div className={styles.subBarLeft}>
          {isAdmin ? (
            <>
              <span className={styles.boardTitle}>Operations board</span>
              <div className={styles.timeFilter}>
                {["upcoming", "past", "all"].map((tf) => (
                  <button
                    key={tf}
                    className={`${styles.timeFilterBtn} ${
                      timeFilter === tf ? styles.timeFilterBtnActive : ""
                    }`}
                    onClick={() => setTimeFilter(tf)}
                  >
                    {tf.charAt(0).toUpperCase() + tf.slice(1)}
                  </button>
                ))}
              </div>
              <div className={styles.quickStats}>
                <span className={styles.dotRed} />
                <span className={styles.quickStatNum}>
                  {needsAttention.length}
                </span>
                <span className={styles.dotAmber} />
                <span className={styles.quickStatNum}>
                  {partiallyFilled.length}
                </span>
                <span className={styles.dotGreen} />
                <span className={styles.quickStatNum}>
                  {fullyStaffed.length}
                </span>
              </div>
            </>
          ) : (
            <>
              <span className={styles.greeting}>Hey {firstName}</span>
              <span className={styles.greetingSub}>
                &middot; {availableJobs.length} shifts open near you
              </span>
            </>
          )}
        </div>
        <div className={styles.subBarRight}>
          <div className={styles.viewSwitcher}>
            {["board", "list", "calendar"].map((v) => (
              <button
                key={v}
                className={`${styles.viewBtn} ${
                  view === v ? styles.viewBtnActive : ""
                }`}
                onClick={() => setView(v)}
              >
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
          <input
            className={styles.searchBox}
            type="text"
            placeholder="Search shifts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {isAdmin && (
            <>
              <button className={styles.adminBtn} onClick={exportCSV}>
                Export CSV
              </button>
              <NavLink to="/jobs-hub/post" className={styles.adminBtnPrimary}>
                + Post Job
              </NavLink>
            </>
          )}
        </div>
      </div>

      {/* Kanban or List */}
      {view === "list" ? (
        <div className={styles.listView}>
          {allListJobs.length === 0 ? (
            <div className={styles.emptyCol}>No shifts to display</div>
          ) : (
            allListJobs.map(({ job, colKey }) => renderCard(job, colKey))
          )}
        </div>
      ) : allEmpty ? (
        <div className={styles.emptyBoard}>
          <h3>No active jobs yet</h3>
          <p>Post your first job to start building the Operations board.</p>
          <NavLink to="/jobs-hub/post" className={styles.adminBtnPrimary}>
            + Post a job
          </NavLink>
        </div>
      ) : (
        <div className={styles.columns}>
          {columns.map((col) => (
            <div key={col.key} className={styles.col}>
              <div className={styles.colHead}>
                <span
                  className={`${styles.colStripe} ${
                    styles[col.stripeClass]
                  }`}
                />
                <span className={styles.colIcon}>{col.icon}</span>
                <span className={styles.colLabel}>{col.label}</span>
                <span
                  className={`${styles.colCount} ${
                    styles[col.countClass]
                  }`}
                >
                  {col.items.length}
                </span>
              </div>
              <div className={styles.cards}>
                {col.items.length === 0 ? (
                  <div className={styles.emptyCol}>
                    No {col.emptyText}
                  </div>
                ) : (
                  col.items.map((job) => renderCard(job, col.key))
                )}
                {/* Pending officer card in Needs Attention column */}
                {isTrulyAdmin &&
                  col.key === "attention" &&
                  pendingOfficers.length > 0 && (
                    <NavLink
                      to="/officers"
                      className={styles.pendingCard}
                    >
                      <div className={styles.pendingCardTitle}>
                        Officer approval request
                      </div>
                      <div className={styles.pendingCardSub}>
                        {pendingOfficers.length} pending &middot; Review
                        on Officers page
                      </div>
                    </NavLink>
                  )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
